import { useEffect, useState } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardMedia from '@mui/material/CardMedia';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Rating from '@mui/material/Rating';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import BuildIcon from '@mui/icons-material/Build';
import CloseIcon from '@mui/icons-material/Close';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import Lightbox, { SlideImage, Slide } from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import Video from 'yet-another-react-lightbox/plugins/video';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import { useTranslation } from 'react-i18next';
import { apiFetch } from '../api/client';
import { eventUrl } from '../lib/slugId';
import { useAuth } from '../auth/AuthContext';
import { PageHeader } from '../components/PageHeader';
import { LogCard } from '../components/LogCard';
import { MarkdownView } from '../components/MarkdownView';
import { MessageOwnerButton } from '../components/MessageOwnerButton';
import { ContributeButton } from '../components/ContributeButton';
import type { Activity, Car, ContributionStatus, Event as EventModel, Paginated, SingleResponse } from '../api/types';

// ─── YouTube helpers ──────────────────────────────────────────────────────
function extractYouTubeId(url: string): string | null {
  const patterns = [
    /youtu\.be\/([^?&#]+)/,
    /youtube\.com\/watch\?v=([^?&#]+)/,
    /youtube\.com\/embed\/([^?&#]+)/,
    /youtube\.com\/shorts\/([^?&#]+)/,
  ];
  for (const re of patterns) {
    const m = url.match(re);
    if (m) return m[1];
  }
  return null;
}

function youtubeThumb(id: string): string {
  return `https://img.youtube.com/vi/${id}/hqdefault.jpg`;
}

// Custom slide type for YouTube embeds (yet-another-react-lightbox supports custom slide kinds via `render.slide`)
interface YouTubeSlide {
  type: 'youtube';
  src: string;
  videoId: string;
  title?: string;
}

type GallerySlide = SlideImage | YouTubeSlide;

function buildSlides(car: Car): GallerySlide[] {
  const slides: GallerySlide[] = car.photo_urls.map((url) => ({ src: url }));
  for (const v of car.videos ?? []) {
    const id = extractYouTubeId(v.url);
    if (!id) continue;
    slides.push({
      type: 'youtube',
      src: v.url,
      videoId: id,
      title: v.title ?? undefined,
    });
  }
  return slides;
}

export function CarDetail() {
  const { slugId } = useParams<{ slugId: string }>();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [car, setCar] = useState<Car | null>(null);
  const [events, setEvents] = useState<EventModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lightboxIndex, setLightboxIndex] = useState<number>(-1);
  const [rating, setRating] = useState(false);

  const handleRate = async (score: number) => {
    if (!car) return;
    setRating(true);
    try {
      const res = await apiFetch<SingleResponse<Car>>(`/api/v1/cars/${car.id}/rating`, {
        method: 'POST',
        body: JSON.stringify({ score }),
      });
      setCar(res.data);
    } finally {
      setRating(false);
    }
  };

  const handleUnrate = async () => {
    if (!car) return;
    setRating(true);
    try {
      const res = await apiFetch<SingleResponse<Car>>(`/api/v1/cars/${car.id}/rating`, {
        method: 'DELETE',
      });
      setCar(res.data);
    } finally {
      setRating(false);
    }
  };

  useEffect(() => {
    if (!slugId) return;
    apiFetch<SingleResponse<Car>>(`/api/v1/cars/${slugId}`)
      .then(async (res) => {
        setCar(res.data);
        const ownerId = res.data.user_id;
        const evts = await apiFetch<Paginated<EventModel>>(
          `/api/v1/events?filter[organizer_id]=${ownerId}&include=organizer&sort=starts_at&per_page=20`
        );
        setEvents(evts.data);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [slugId]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !car) {
    return <Alert severity="error">{error || t('carDetail.notFound')}</Alert>;
  }

  const now = new Date();
  const activities = car.activities ?? [];
  const upcoming = activities
    .filter((a: Activity) => a.scheduled_at && new Date(a.scheduled_at) >= now)
    .sort((a, b) => new Date(a.scheduled_at!).getTime() - new Date(b.scheduled_at!).getTime());
  const history = activities
    .filter((a: Activity) => !a.scheduled_at || new Date(a.scheduled_at) < now)
    .sort((a, b) => new Date(b.scheduled_at ?? b.created_at).getTime() - new Date(a.scheduled_at ?? a.created_at).getTime());

  const upcomingEvents = events.filter((e) => new Date(e.starts_at) >= now);

  const isOwner = user?.id === car.user_id;

  const slides = buildSlides(car);
  const photoCount = car.photo_urls.length;

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 } }}>
      <PageHeader
        crumbs={[
          { label: t('nav.explore'), to: '/explore' },
          { label: car.display_name },
        ]}
      />
      <Stack spacing={4}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
      >
        <Box>
          <Typography variant="h3">{car.display_name}</Typography>
          <Typography variant="h6" color="text.secondary">
            {car.year} {car.make} {car.model}
          </Typography>
        </Box>
        {isOwner ? (
          <Button
            variant="outlined"
            startIcon={<BuildIcon />}
            component={RouterLink}
            to={`/garage/${car.slug}-${car.id}`}
          >
            {t('carDetail.manageInGarage')}
          </Button>
        ) : (
          user && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              useFlexGap
              sx={{ flexWrap: 'wrap' }}
            >
              <ContributeButton
                carId={car.id}
                initialStatus={car.my_contribution_status}
                onStatusChange={(status: ContributionStatus) =>
                  setCar((prev) =>
                    prev
                      ? {
                          ...prev,
                          my_contribution_status: status,
                          can_contribute: status === 'approved',
                        }
                      : prev
                  )
                }
              />
              <MessageOwnerButton car={car} />
            </Stack>
          )
        )}
      </Stack>

      {car.summary && <MarkdownView>{car.summary}</MarkdownView>}

      {slides.length > 0 && (
        <>
          <Typography variant="h5">{t('carDetail.gallery')}</Typography>
          <Grid container spacing={2}>
            {slides.map((slide, idx) => {
              const isVideo = 'type' in slide && slide.type === 'youtube';
              const thumb = isVideo
                ? youtubeThumb(slide.videoId)
                : (slide as SlideImage).src;
              return (
                <Grid key={idx} size={{ xs: 12, sm: 6, md: 4 }}>
                  <Card>
                    <CardActionArea onClick={() => setLightboxIndex(idx)}>
                      <Box sx={{ position: 'relative' }}>
                        <CardMedia
                          component="img"
                          height="200"
                          image={thumb}
                          alt={
                            isVideo
                              ? slide.title ?? `${car.display_name} video ${idx - photoCount + 1}`
                              : `${car.display_name} photo ${idx + 1}`
                          }
                        />
                        {isVideo && (
                          <Box
                            sx={{
                              position: 'absolute',
                              inset: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              bgcolor: 'rgba(0, 0, 0, 0.35)',
                              color: 'common.white',
                            }}
                          >
                            <PlayCircleOutlineIcon sx={{ fontSize: 72 }} />
                          </Box>
                        )}
                      </Box>
                    </CardActionArea>
                  </Card>
                </Grid>
              );
            })}
          </Grid>

          <Lightbox
            open={lightboxIndex >= 0}
            close={() => setLightboxIndex(-1)}
            index={lightboxIndex >= 0 ? lightboxIndex : 0}
            slides={slides as Slide[]}
            plugins={[Zoom, Thumbnails, Video]}
            zoom={{ maxZoomPixelRatio: 4, scrollToZoom: true }}
            thumbnails={{ position: 'bottom', width: 120, height: 80 }}
            render={{
              slide: ({ slide }) => {
                const maybeYt = slide as unknown as YouTubeSlide;
                if (maybeYt?.type === 'youtube') {
                  return (
                    <Box
                      sx={{
                        width: '90vw',
                        maxWidth: 1200,
                        aspectRatio: '16/9',
                      }}
                    >
                      <iframe
                        src={`https://www.youtube.com/embed/${maybeYt.videoId}?rel=0`}
                        title={maybeYt.title ?? 'YouTube video'}
                        width="100%"
                        height="100%"
                        style={{ border: 0 }}
                        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                      />
                    </Box>
                  );
                }
                return undefined; // fall back to default rendering for images
              },
            }}
          />
        </>
      )}

      <Divider />

      {/* Rating section — visible to everyone; interactive for non-owners */}
      <Typography variant="h5">{t('carDetail.ratingHeading')}</Typography>
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          {t('carDetail.ratingExplanation')}
        </Typography>
        {car.rating_count > 0 ? (
          <Stack direction="row" spacing={1.5} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
            <Rating
              value={car.rating_average}
              precision={0.1}
              readOnly
              icon={<BuildIcon fontSize="inherit" />}
              emptyIcon={<BuildIcon fontSize="inherit" />}
            />
            <Typography variant="body2" color="text.secondary">
              {t('carDetail.ratingSummary', {
                average: car.rating_average.toFixed(1),
                count: car.rating_count,
              })}
            </Typography>
          </Stack>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {t('carDetail.ratingNoneYet')}
          </Typography>
        )}

        {!isOwner && (
          <Stack direction="row" spacing={1} alignItems="center" useFlexGap sx={{ flexWrap: 'wrap' }}>
            <Typography variant="body2" color="text.secondary">
              {t('carDetail.yourRating')}
            </Typography>
            <Tooltip title={t('carDetail.removeRating')}>
              <span>
                <IconButton
                  size="small"
                  onClick={handleUnrate}
                  disabled={rating || !car.my_rating}
                  aria-label={t('carDetail.removeRating')}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Rating
              value={car.my_rating ?? null}
              disabled={rating}
              size="large"
              onChange={(_, value) => {
                if (value !== null) handleRate(value);
              }}
              icon={<BuildIcon fontSize="inherit" />}
              emptyIcon={<BuildIcon fontSize="inherit" />}
            />
          </Stack>
        )}
      </Stack>

      <Divider />

      <Typography variant="h5">{t('carDetail.activityHeading', { name: car.display_name })}</Typography>
      {upcoming.length === 0 ? (
        <Typography color="text.secondary">{t('carDetail.noUpcoming')}</Typography>
      ) : (
        <Stack spacing={2}>
          {upcoming.map((act) => (
            <LogCard
              key={act.id}
              date={act.scheduled_at ?? act.created_at}
              title={t(`activityType.${act.type}`, { defaultValue: act.type_label })}
              description={<MarkdownView>{act.summary}</MarkdownView>}
            />
          ))}
        </Stack>
      )}

      <Divider />

      <Typography variant="h5">{t('carDetail.historyHeading', { name: car.display_name })}</Typography>
      {history.length === 0 ? (
        <Typography color="text.secondary">{t('carDetail.noPast')}</Typography>
      ) : (
        <Stack spacing={2}>
          {history.map((act) => (
            <LogCard
              key={act.id}
              date={act.scheduled_at ?? act.created_at}
              title={t(`activityType.${act.type}`, { defaultValue: act.type_label })}
              description={<MarkdownView>{act.summary}</MarkdownView>}
            />
          ))}
        </Stack>
      )}

      <Divider />

      <Typography variant="h5">{t('carDetail.eventsHeading', { name: car.user?.name ?? t('carDetail.owner') })}</Typography>
      {upcomingEvents.length === 0 ? (
        <Typography color="text.secondary">{t('carDetail.noEvents')}</Typography>
      ) : (
        <Stack spacing={2}>
          {upcomingEvents.map((evt) => (
            <LogCard
              key={evt.id}
              date={evt.starts_at}
              title={evt.title}
              chip={evt.type_label}
              location={`${evt.location_name}${evt.city ? ', ' + evt.city : ''}`}
              photo={evt.photo_urls[0]}
              to={eventUrl(evt)}
            />
          ))}
        </Stack>
      )}
      </Stack>
    </Container>
  );
}
